"use client";

import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import apiEndpoints from "../../apiConfig"; // Make sure path is correct
function ForgotPasswordDialog({ open, onClose, initialEmail }) {
  const [resetEmail, setResetEmail] = useState(initialEmail || "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleSubmitEmail = async () => {
    if (!resetEmail) {
      setError("Please enter your email or phone");
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${apiEndpoints.login}`, // use apiEndpoints.login
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "check_user",
            identifier: resetEmail,
          }),
        },
      );

      const data = await response.json();
      setLoading(false);

      if (!data.success) {
        setError(data.message || "No user found with this email or phone");
        return;
      }

      // ✅ Email exists, now show password fields
      setEmailSubmitted(true);
      setError("");
    } catch (err) {
      setLoading(false);
      setError("Server error. Please try again later.");
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      setError("Please fill in all password fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    // ✅ Password strength validation
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{6,}$/; // min 6 chars
    if (!passwordRegex.test(newPassword)) {
      setError(
        "Password must include: uppercase letter, lowercase letter, number, and special character (e.g., !@#$%)",
      );

      return;
    }

    try {
      setLoading(true);
      const response = await fetch(apiEndpoints.login, {
        // use apiEndpoints.login
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "reset_password",
          identifier: resetEmail,
          new_password: newPassword,
        }),
      });

      const data = await response.json();
      setLoading(false);

      if (data.success) {
        setTimeout(() => {
          onClose();
          navigate("/");
        }, 800);
      } else {
        setError(data.message || "Failed to reset password");
      }
    } catch (err) {
      setLoading(false);
      setError("Server error. Please try again later.");
    }
  };

  useEffect(() => {
    if (!open) {
      setResetEmail(initialEmail || "");
      setNewPassword("");
      setConfirmPassword("");
      setEmailSubmitted(false);
      setError("");
      setLoading(false);
    }
  }, [open, initialEmail]);

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1300,
      }}
    >
      <Card
        sx={{ width: "100%", maxWidth: 400, mx: "auto", position: "relative" }}
      >
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "black" }}
        >
          <Close />
        </IconButton>

        <CardContent sx={{ p: 4, pt: 6 }}>
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 3, textAlign: "center", color: "black" }}
          >
            Change Password
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {!emailSubmitted && (
              <>
                <TextField
                  fullWidth
                  label="Email or Phone"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                />
                <Button
                  onClick={handleSubmitEmail}
                  variant="contained"
                  sx={{ bgcolor: "black", py: 1.5 }}
                  disabled={!resetEmail || loading}
                >
                  {loading ? "Checking..." : "Next"}
                </Button>
              </>
            )}

            {emailSubmitted && (
              <>
                <TextField
                  fullWidth
                  label="New Password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <Button
                  onClick={handleResetPassword}
                  variant="contained"
                  sx={{ bgcolor: "black", py: 1.5 }}
                  disabled={loading || !newPassword || !confirmPassword}
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </>
            )}

            {error && (
              <Typography
                variant="body2"
                color="error"
                sx={{ textAlign: "center" }}
              >
                {error}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </div>
  );
}

export default ForgotPasswordDialog;
